import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  TablePagination,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import TemplateFilters from "../../../components/Admin/Templates/TemplateFilters";
import TemplateTable from "../../../components/Admin/Templates/TemplateTable";
import TemplateForm from "../../../components/Admin/Templates/TemplateForm";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  createTemplate,
  editTemplate,
  uploadFile,
  deleteTemplate,
  disableTemplate,
  enableTemplate,
  publishTemplate,
  generateTemplateVersion,
} from "../../../api/bpm/endpoints";
import type { TemplateDto } from "../../../api/bpm/model";
import { useAuth } from "../../../auth";
import {
  getListTemplateQueryKey,
  getListTemplateQueryOptions,
} from "../../../api/bpm/endpoints";

const templatesSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
});

export const Route = createFileRoute("/__auth/admin/templates")({
  component: TemplatesComponent,
  validateSearch: (search) => templatesSearchSchema.parse(search),
  staticData: {
    title: "documentTemplates.title",
  },
});

function TemplatesComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDto | null>(
    null,
  );
  const [templateToDelete, setTemplateToDelete] = useState<TemplateDto | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const { currentTenant } = useAuth();
  const tenant = currentTenant || "default-domain";
  const templatesQueryKey = getListTemplateQueryKey(tenant);

  const refreshTemplates = () =>
    queryClient.invalidateQueries({
      queryKey: templatesQueryKey,
    });

  const {
    data: templatesData,
    isLoading,
    error,
  } = useQuery({
    ...getListTemplateQueryOptions(tenant, {
      page: (search.page || 1) - 1,
      size: search.size || 10,
      q: search.q || undefined,
    } as any),
    staleTime: 30000,
  });

  const data = templatesData as any;
  const rows: TemplateDto[] = useMemo(() => data?.content || [], [data]);

  const handlePageChange = (_: unknown, newPage: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: newPage + 1,
      }),
    });
  };

  const handleFilter = (newFilters: { q?: string }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1,
      }),
    });
  };

  const handleApiError = (error: any) => {
    const apiMessage = error?.response?.data?.message || error?.message || "";
    let errorMessage = t("documentTemplates.saveError");

    if (apiMessage) {
      if (apiMessage.includes("editar um template publicado")) {
        errorMessage = t("documentTemplates.errors.editPublished");
      } else if (apiMessage.includes("campos estão inválidos")) {
        errorMessage = t("documentTemplates.errors.invalidFields");
      } else {
        errorMessage = apiMessage;
      }
    }

    showSnackbar(errorMessage, "error");
  };

  const saveMutation = useMutation({
    mutationFn: async (input: any) => {
      let documentId = selectedTemplate?.documentId;

      if (input.file) {
        const uploadResponse = await uploadFile(tenant, {
          file: input.file,
        } as any);

        const uploadRes = (uploadResponse.data ?? uploadResponse) as any;

        documentId =
          uploadRes?.fileUid || uploadRes?.id || uploadRes?.documentId;
      }

      const payload = {
        name: input.name,
        description: input.description,
        documentType: {
          id: input.documentType?.id || input.documentType,
        },
        documentId: documentId,
        documentTitle: input.name,
        documentDescription: input.description,
        draft: false,
      };

      if (selectedTemplate?.id) {
        return editTemplate(tenant, selectedTemplate.id, payload as any);
      }

      return createTemplate(tenant, payload as any);
    },
    onSuccess: () => {
      refreshTemplates();
      showSnackbar(
        selectedTemplate?.id
          ? t("documentTemplates.updateSuccess")
          : t("documentTemplates.createSuccess"),
      );
      setIsDialogOpen(false);
    },
    onError: (error: any) => handleApiError(error),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => deleteTemplate(tenant, id),
    onSuccess: () => {
      refreshTemplates();
      showSnackbar(t("documentTemplates.deleteSuccess"));
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (item: TemplateDto) => {
      if (item.enabled) return disableTemplate(tenant, item.id!);
      return enableTemplate(tenant, item.id!);
    },
    onSuccess: refreshTemplates,
  });

  const publishMutation = useMutation({
    mutationFn: async (id: number) => publishTemplate(tenant, id),
    onSuccess: () => {
      refreshTemplates();
      showSnackbar(t("documentTemplates.publishSuccess"));
    },
  });

  const versionMutation = useMutation({
    mutationFn: async (id: number) => generateTemplateVersion(tenant, id),
    onSuccess: () => {
      refreshTemplates();
      showSnackbar(t("documentTemplates.versionSuccess"));
    },
  });

  const handleHistory = (template: TemplateDto) => {
    navigate({
      to: '/admin/audit-logs' as any,
      search: { resourceType: 'TEMPLATE', resourceId: template.code } as any
    });
  };

  return (
    <ListLayout
      title={t("documentTemplates.title")}
      subtitle={t("documentTemplates.subtitle")}
      filters={
        <TemplateFilters
          onApply={handleFilter}
          isLoading={isLoading}
          initialValues={{ q: search.q }}
        />
      }
      isLoading={isLoading}
      isEmpty={!rows.length}
      error={error as any}
      actions={
        <IconButton
          color="primary"
          onClick={() => {
            setSelectedTemplate(null);
            setIsDialogOpen(true);
          }}
        >
          <AddIcon />
        </IconButton>
      }
    >
      <TemplateTable
        tenant={tenant}
        data={rows}
        isLoading={isLoading}
        onEdit={(item) => {
          setSelectedTemplate(item);
          setIsDialogOpen(true);
        }}
        onDelete={(item) => {
          setTemplateToDelete(item);
          setIsDeleteDialogOpen(true);
        }}
        onToggleEnable={(item) => toggleStatusMutation.mutate(item)}
        onPublish={(item) => publishMutation.mutate(item.id!)}
        onGenerateVersion={(item) => versionMutation.mutate(item.id!)}
        onHistory={handleHistory}
      />

      <TablePagination
        component="div"
        count={data?.totalElements || 0}
        page={(search.page || 1) - 1}
        onPageChange={handlePageChange}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          navigate({
            search: (prev) => ({
              ...prev,
              size: parseInt(e.target.value, 10),
              page: 1,
            }),
          });
        }}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t("common.of")} ${count !== -1 ? count : `${t("common.moreThan")} ${to}`}`
        }
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      />

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <TemplateForm
            initialValues={selectedTemplate || undefined}
            onSubmit={(vals) => saveMutation.mutate(vals)}
            onCancel={() => setIsDialogOpen(false)}
            isLoading={saveMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("documentTemplates.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("documentTemplates.deleteConfirm", {
              name: templateToDelete?.name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (templateToDelete?.id) {
                deleteMutation.mutate(templateToDelete.id);
              }
              setIsDeleteDialogOpen(false);
              setTemplateToDelete(null);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
